import { TransText } from '@/i18n/trans/text';
import type { ServiceAttributeDTO } from '@/types/dto/agent';
import { breakpoints } from '@/utils/breakpoints';
import { cn } from '@/utils/cn';
import { Button, Icons, Popover, Sheet, Typography } from '@fleet/webshop-ui';
import { type FC, useState } from 'react';
import { useMediaQuery } from 'react-responsive';

interface ServiceTextButtonProps {
  serviceTexts: Array<ServiceAttributeDTO>;
}

const ServiceTextButton: FC<ServiceTextButtonProps> = ({ serviceTexts }) => {
  const [isOpen, setIsOpen] = useState(false);
  const isLaptopOrBigger = useMediaQuery({
    minWidth: breakpoints.laptop,
  });

  if (!serviceTexts.length) {
    return null;
  }

  const title =
    serviceTexts.length === 1 ? (
      serviceTexts[0].shortDescription
    ) : (
      <TransText i18nKey="importantInformation" />
    );

  const renderTrigger = (alignmentClassName: string) => {
    const ChevronComponent = isOpen ? Icons.chevronUp : Icons.chevronDown;

    return (
      <Button
        variant="ghost"
        data-testid="service-text-trigger"
        className={cn('h-auto p-0', alignmentClassName)}
      >
        <div className="grid grid-cols-[1fr_auto_1fr] gap-1 rounded border border-warning-border bg-warning-surface px-1.5 py-1 text-dark">
          <Icons.infoCircle height={16} width={16} />
          <Typography variant="body2" className="truncate">
            {title}
          </Typography>
          <ChevronComponent height={16} width={16} />
        </div>
      </Button>
    );
  };

  const renderDescriptions = () => (
    <div className="flex flex-col gap-3">
      {serviceTexts.map(({ description }, key) => (
        <Typography key={key} variant="paragraph">
          {description}
        </Typography>
      ))}
    </div>
  );

  if (isLaptopOrBigger) {
    const popoverContent = (
      <div className="flex flex-col gap-4">
        <div className="flex gap-3">
          <Icons.infoCircle height={20} width={20} />
          <Typography variant="subtitle">{title}</Typography>
        </div>
        {renderDescriptions()}
      </div>
    );

    return (
      <Popover
        isOpen={isOpen}
        onOpenChange={setIsOpen}
        trigger={renderTrigger('self-end')}
        content={popoverContent}
        data-testid="service-text-popover"
        className={{ content: 'w-[360px] p-4 text-dark' }}
      />
    );
  }

  return (
    <Sheet
      isOpen={isOpen}
      onOpenChange={setIsOpen}
      trigger={renderTrigger('self-start')}
      side="bottom"
      headerIcon={<Icons.infoCircle height={20} width={20} />}
      headerTitle={title}
      className={{ content: 'p-0' }}
      data-testid="service-text-sheet"
    >
      <div className="p-4 pt-0">{renderDescriptions()}</div>
    </Sheet>
  );
};

export default ServiceTextButton;
